import React, { useState, useEffect, useRef, useCallback } from 'react';
import './App.css';

const API = 'https://m290ll7hqb.execute-api.us-east-1.amazonaws.com';

const RESERVATION_ROUTES = [
  'Anacortes to Lopez Island',
  'Anacortes to Orcas Island',
  'Anacortes to Friday Harbor',
  'Anacortes to Shaw Island',
  'Anacortes to Sidney BC',
  'Lopez Island to Anacortes',
  'Orcas Island to Anacortes',
  'Friday Harbor to Anacortes',
  'Friday Harbor to Sidney BC',
  'Shaw Island to Anacortes',
  'Sidney BC to Anacortes',
  'Sidney BC to Friday Harbor',
  'Port Townsend to Coupeville',
  'Coupeville to Port Townsend',
];

function uid() { return Math.random().toString(36).slice(2, 10); }

async function apiFetch(path, opts = {}) {
  const res = await fetch(`${API}${path}`, {
    headers: { 'Content-Type': 'application/json' },
    ...opts,
  });
  const data = await res.json();
  if (!res.ok) throw new Error(data.error || `HTTP ${res.status}`);
  return data;
}

// Cookie helpers
function setCookie(name, value, days) {
  const d = new Date();
  d.setTime(d.getTime() + days * 24 * 60 * 60 * 1000);
  document.cookie = `${name}=${encodeURIComponent(value)};expires=${d.toUTCString()};path=/;SameSite=Lax;Secure`;
}
function getCookie(name) {
  const match = document.cookie.match(new RegExp(`(?:^|; )${name}=([^;]*)`));
  return match ? decodeURIComponent(match[1]) : null;
}
function deleteCookie(name) {
  document.cookie = `${name}=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=/`;
}

// ── Login Page ────────────────────────────────────────────────────────────

function LoginPage({ onLogin }) {
  const [phone, setPhone] = useState('');
  const [remember, setRemember] = useState(true);
  const [step, setStep] = useState('phone'); // 'phone' | 'otp'
  const [otp, setOtp] = useState('');
  const [sending, setSending] = useState(false);
  const [error, setError] = useState('');
  const [sessionId, setSessionId] = useState('');

  const fullPhone = `+1${phone.replace(/\D/g, '')}`;

  const handleSendOTP = async (e) => {
    e.preventDefault();
    const digits = phone.replace(/\D/g, '');
    if (!digits || digits.length !== 10) return setError('Please enter a valid 10-digit phone number.');
    setError('');
    setSending(true);
    try {
      const data = await apiFetch('/api/auth/send-otp', {
        method: 'POST',
        body: JSON.stringify({ whatsappNumber: fullPhone }),
      });
      setSessionId(data.sessionId);
      setStep('otp');
    } catch (e) {
      setError(e.message);
    } finally {
      setSending(false);
    }
  };

  const handleVerifyOTP = async (e) => {
    e.preventDefault();
    if (!otp || otp.length !== 4) return setError('Enter the 4-digit code.');
    setError('');
    setSending(true);
    try {
      await apiFetch('/api/auth/verify-otp', {
        method: 'POST',
        body: JSON.stringify({ whatsappNumber: fullPhone, sessionId, code: otp }),
      });
      if (remember) {
        setCookie('ferry_auth_phone', fullPhone, 30);
      } else {
        deleteCookie('ferry_auth_phone');
      }
      onLogin(fullPhone);
    } catch (e) {
      setError(e.message);
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="app">
      <header>
        <h1>⛴️ WA Ferry Tracker</h1>
        <p className="subtitle">Sign in with your WhatsApp number</p>
      </header>

      <div className="card">
        {step === 'phone' ? (
          <form onSubmit={handleSendOTP}>
            <h2>Login</h2>
            <div className="field">
              <label>WhatsApp Number</label>
              <div className="phone-input-row">
                <span className="country-code">+1</span>
                <input
                  type="tel"
                  placeholder="2065551234"
                  value={phone}
                  onChange={e => setPhone(e.target.value.replace(/\D/g, '').slice(0, 10))}
                  autoFocus
                  required
                  inputMode="numeric"
                />
              </div>
              <p className="hint">We'll send a 4-digit code to verify your number.</p>
            </div>

            <div className="field checkbox-field">
              <label className="checkbox-label">
                <input
                  type="checkbox"
                  checked={remember}
                  onChange={e => setRemember(e.target.checked)}
                />
                <span>Remember me for 30 days</span>
              </label>
            </div>

            {error && <div className="inline-msg error">{error}</div>}

            <button type="submit" className="btn btn-primary btn-full" disabled={sending}>
              {sending ? 'Sending…' : 'Send Code'}
            </button>
          </form>
        ) : (
          <form onSubmit={handleVerifyOTP}>
            <h2>Enter Code</h2>
            <p className="otp-sent-msg">A 4-digit code was sent to <strong>{fullPhone}</strong></p>

            <div className="field">
              <label>Verification Code</label>
              <input
                type="text"
                inputMode="numeric"
                pattern="[0-9]*"
                maxLength={4}
                placeholder="0000"
                value={otp}
                onChange={e => setOtp(e.target.value.replace(/\D/g, '').slice(0, 4))}
                autoFocus
                required
                className="otp-input"
              />
            </div>

            {error && <div className="inline-msg error">{error}</div>}

            <button type="submit" className="btn btn-primary btn-full" disabled={sending}>
              {sending ? 'Verifying…' : 'Verify & Login'}
            </button>

            <button
              type="button"
              className="clear-link"
              onClick={() => { setStep('phone'); setOtp(''); setError(''); }}
            >
              ← Change number
            </button>
          </form>
        )}
      </div>
    </div>
  );
}

// ── Main App ──────────────────────────────────────────────────────────────

function FerryTracker({ userPhone, onLogout }) {
  const [jobs, setJobs] = useState([]);
  const [form, setForm] = useState({
    ferryRoute: RESERVATION_ROUTES[0],
    ferryDate: '',
    targetTimes: [],
    frequency: 5,
  });
  const [sailings, setSailings] = useState([]);
  const [loadingSailings, setLoadingSailings] = useState(false);
  const [formError, setFormError] = useState('');
  const [starting, setStarting] = useState(false);
  const pollRef = useRef(null);

  const isAdmin = userPhone === '+12067519422';

  const fetchJobs = useCallback(async () => {
    try {
      const data = await apiFetch('/api/jobs');
      const allJobs = data.jobs || [];
      const filtered = isAdmin ? allJobs : allJobs.filter(j => j.whatsappNumber === userPhone);
      setJobs(filtered.sort((a, b) => (b.createdAt || '').localeCompare(a.createdAt || '')));
    } catch (e) { console.error('Fetch jobs failed:', e); }
  }, [userPhone, isAdmin]);

  useEffect(() => {
    fetchJobs();
    pollRef.current = setInterval(fetchJobs, 15000);
    return () => clearInterval(pollRef.current);
  }, [fetchJobs]);

  useEffect(() => {
    if (!form.ferryDate || !form.ferryRoute) { setSailings([]); return; }
    setLoadingSailings(true);
    setForm(f => ({ ...f, targetTimes: [] }));
    apiFetch(`/api/sailings?route=${encodeURIComponent(form.ferryRoute)}&date=${form.ferryDate}`)
      .then(d => setSailings(d.sailings || []))
      .catch(() => setSailings([]))
      .finally(() => setLoadingSailings(false));
  }, [form.ferryDate, form.ferryRoute]);

  const handleStart = async (e) => {
    e.preventDefault();
    setFormError('');
    if (!form.ferryDate) return setFormError('Please select a travel date.');
    setStarting(true);
    try {
      await apiFetch('/api/start', {
        method: 'POST',
        body: JSON.stringify({ ...form, whatsappNumber: userPhone, jobId: uid() }),
      });
      await fetchJobs();
      setForm(f => ({ ...f, ferryDate: '', targetTimes: [] }));
      setSailings([]);
    } catch (e) {
      setFormError(e.message);
    } finally {
      setStarting(false);
    }
  };

  const handleStop = async (jobId) => {
    try {
      await apiFetch('/api/stop', { method: 'POST', body: JSON.stringify({ jobId }) });
      await fetchJobs();
    } catch (e) { alert(e.message); }
  };

  const handleCheckNow = async (job) => {
    try {
      await apiFetch('/api/check-now', {
        method: 'POST',
        body: JSON.stringify({ jobId: job.jobId, ferryDate: job.ferryDate, ferryRoute: job.ferryRoute, targetTimes: job.targetTimes || [], whatsappNumber: job.whatsappNumber }),
      });
      await fetchJobs();
    } catch (e) { alert(e.message); }
  };

  const today = new Date().toISOString().split('T')[0];
  const activeJobs  = jobs.filter(j => j.running);

  const [showContact, setShowContact] = useState(false);

  return (
    <div className="app">
      <div className="topbar">
        <button className="topbar-btn" onClick={() => setShowContact(true)}>Contact Us</button>
      </div>
      <header>
        <h1>⛴️ WA Ferry Tracker</h1>
        <div className="user-bar">
          <span className="user-phone">{userPhone}</span>
          <button className="clear-link" onClick={onLogout}>Logout</button>
        </div>
      </header>

      {showContact && <ContactModal userPhone={userPhone} onClose={() => setShowContact(false)} />}

      <div className="card">
        <h2>New Tracking Job</h2>
        <form onSubmit={handleStart}>

          <div className="field">
            <label>Ferry Route</label>
            <select value={form.ferryRoute} onChange={e => setForm(f => ({ ...f, ferryRoute: e.target.value }))}>
              {RESERVATION_ROUTES.map(r => <option key={r}>{r}</option>)}
            </select>
            <p className="hint">Only routes that support vehicle reservations are listed.</p>
          </div>

          <div className="field">
            <label>Travel Date</label>
            <input
              type="date" min={today} value={form.ferryDate}
              onChange={e => setForm(f => ({ ...f, ferryDate: e.target.value }))}
              required
            />
          </div>

          <div className="field">
            <label>
              Sailing Times {loadingSailings && <span className="loading-pill">loading…</span>}
              {sailings.length > 0 && <span className="hint-inline">{form.targetTimes.length === 0 ? 'Any time' : `${form.targetTimes.length} selected`}</span>}
            </label>
            {sailings.length > 0 ? (
              <div className="time-grid">
                {sailings.map(s => {
                  const selected = form.targetTimes.includes(s.time24);
                  return (
                    <button
                      key={s.time24}
                      type="button"
                      className={`time-btn ${selected ? 'time-btn-active' : ''}`}
                      onClick={() => setForm(f => ({
                        ...f,
                        targetTimes: selected
                          ? f.targetTimes.filter(t => t !== s.time24)
                          : [...f.targetTimes, s.time24],
                      }))}
                    >
                      {s.time}
                      <span className="time-vessel">{s.vessel}</span>
                    </button>
                  );
                })}
              </div>
            ) : (
              <p className="hint">{form.ferryDate ? (loadingSailings ? 'Fetching sailings…' : 'No sailings found for this date.') : 'Select a date to see sailing times.'}</p>
            )}
            {sailings.length > 0 && form.targetTimes.length > 0 && (
              <button type="button" className="clear-link" onClick={() => setForm(f => ({ ...f, targetTimes: [] }))}>
                Clear — watch any time
              </button>
            )}
          </div>

          <div className="field">
            <label>Check Frequency (minutes)</label>
            <input
              type="number" min={1} max={60} value={form.frequency}
              onChange={e => setForm(f => ({ ...f, frequency: Number(e.target.value) }))}
            />
          </div>

          {formError && <div className="inline-msg error">{formError}</div>}

          <button type="submit" className="btn btn-primary btn-full" disabled={starting}>
            {starting ? 'Starting…' : '▶ Start Tracking'}
          </button>
        </form>
      </div>

      {activeJobs.length > 0 && (
        <div className="card">
          <h2>Active Jobs</h2>
          {activeJobs.map(job => (
            <JobCard key={job.jobId} job={job} onStop={handleStop} onCheckNow={handleCheckNow} onRefresh={fetchJobs} />
          ))}
        </div>
      )}

    </div>
  );
}

function JobCard({ job, onStop, onCheckNow, onRefresh }) {
  const [checking, setChecking] = useState(false);
  const [showWatchers, setShowWatchers] = useState(false);
  const status = job.lastStatus && typeof job.lastStatus === 'string'
    ? (() => { try { return JSON.parse(job.lastStatus); } catch { return null; } })()
    : job.lastStatus;

  const doCheck = async () => {
    setChecking(true);
    await onCheckNow(job);
    setChecking(false);
  };

  const watchers = job.watchers || [];

  return (
    <div className={`job-card ${job.running ? 'job-running' : 'job-stopped'}`}>
      <div className="job-header">
        <div className="job-title">
          <strong>{job.ferryRoute}</strong>
          <span className="job-meta-inline">
            {job.ferryDate} · Every {job.frequency}m
          </span>
          {job.running && status?.sailings?.length > 0 && (
            <div className="time-tags">
              {(job.targetTimes?.length > 0 ? job.targetTimes : status.sailings.map(s => s.time24 || s.time)).map(t => (
                <span key={t} className="time-tag">
                  {t}
                  <button className="time-tag-remove" onClick={async (e) => {
                    e.stopPropagation();
                    if (!window.confirm(`Remove ${t} from tracking?`)) return;
                    const currentTimes = job.targetTimes?.length > 0
                      ? job.targetTimes
                      : status.sailings.map(s => s.time24 || s.time);
                    const updated = currentTimes.filter(x => x !== t);
                    await apiFetch('/api/update-times', { method: 'POST', body: JSON.stringify({ jobId: job.jobId, targetTimes: updated }) });
                    if (onRefresh) onRefresh();
                  }}>×</button>
                </span>
              ))}
            </div>
          )}
          {job.running && !status?.sailings?.length && (!job.targetTimes || job.targetTimes.length === 0) && (
            <span className="job-meta-inline">Any time</span>
          )}
          {!job.running && job.targetTimes?.length > 0 && (
            <span className="job-meta-inline">{job.targetTimes.join(', ')}</span>
          )}
        </div>
        <span className={`badge ${job.running ? 'badge-green' : 'badge-gray'}`}>
          {job.running ? '● Live' : '○ Off'}
        </span>
      </div>

      {job.lastChecked && (
        <p className="last-checked">Last checked: {new Date(job.lastChecked).toLocaleTimeString()}</p>
      )}

      {status && (
        <div className={`avail-box ${status.available ? 'avail-yes' : 'avail-no'}`}>
          <strong>{status.available ? '✅ Space Available!' : '❌ No Space Yet'}</strong>
          {status.sailings && status.sailings.length > 0 && (
            <div className="sailings-table">
              {status.sailings.map((s, i) => (
                <div key={i} className={`sailing-row ${s.available ? 'sail-avail' : ''}`}>
                  <span className="sail-time">{s.time}</span>
                  <span className="sail-vessel">{s.vessel}</span>
                  <span className={`sail-spaces ${s.available ? 'spaces-yes' : ''}`}>{s.status || (s.available ? 'Space Available' : 'No Space')}</span>
                </div>
              ))}
            </div>
          )}
          {!status.sailings?.length && <p className="avail-detail">{status.details}</p>}
          {status.available && (
            <a
              href="https://secureapps.wsdot.wa.gov/ferries/reservations/vehicle/SailingSchedule.aspx?cookieCheck=true"
              target="_blank" rel="noreferrer"
              className="btn btn-primary book-btn"
            >
              Book Now →
            </a>
          )}
        </div>
      )}

      {job.running && (
        <div className="job-actions">
          <button className="btn btn-secondary" onClick={doCheck} disabled={checking}>
            {checking ? 'Checking…' : '↻ Check Now'}
          </button>
          <button className="btn btn-secondary" onClick={() => setShowWatchers(true)}>
            👥 Watchers{watchers.length > 0 && ` (${watchers.length})`}
          </button>
          <button className="btn btn-danger" onClick={() => onStop(job.jobId)}>■ Stop</button>
        </div>
      )}

      {showWatchers && (
        <WatchersModal
          jobId={job.jobId}
          watchers={watchers}
          onClose={() => setShowWatchers(false)}
          onUpdated={onRefresh}
        />
      )}
    </div>
  );
}

function WatchersModal({ jobId, watchers: initialWatchers, onClose, onUpdated }) {
  const [watchers, setWatchers] = useState(initialWatchers || []);
  const [newNumber, setNewNumber] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const handleAdd = async () => {
    const digits = newNumber.replace(/\D/g, '');
    if (digits.length !== 10) return setError('Enter a valid 10-digit number.');
    const fullNum = `+1${digits}`;
    if (watchers.includes(fullNum)) return setError('Already added.');
    setError('');
    setSaving(true);
    try {
      const updated = [...watchers, fullNum];
      await apiFetch('/api/watchers', {
        method: 'POST',
        body: JSON.stringify({ jobId, watchers: updated }),
      });
      setWatchers(updated);
      setNewNumber('');
      if (onUpdated) onUpdated();
    } catch (e) {
      setError(e.message);
    } finally {
      setSaving(false);
    }
  };

  const handleRemove = async (num) => {
    setSaving(true);
    try {
      const updated = watchers.filter(w => w !== num);
      await apiFetch('/api/watchers', {
        method: 'POST',
        body: JSON.stringify({ jobId, watchers: updated }),
      });
      setWatchers(updated);
      if (onUpdated) onUpdated();
    } catch (e) {
      setError(e.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" onClick={e => e.stopPropagation()}>
        <h3>Notify Others</h3>
        <p className="hint">These numbers will also get a WhatsApp message when space opens up.</p>

        {watchers.length > 0 && (
          <div className="watcher-list">
            {watchers.map(w => (
              <div key={w} className="watcher-item">
                <span>{w}</span>
                <button className="watcher-remove" onClick={() => handleRemove(w)} disabled={saving}>×</button>
              </div>
            ))}
          </div>
        )}

        <div className="watcher-add">
          <div className="phone-input-row">
            <span className="country-code">+1</span>
            <input
              type="tel"
              placeholder="2065551234"
              value={newNumber}
              onChange={e => setNewNumber(e.target.value.replace(/\D/g, '').slice(0, 10))}
              inputMode="numeric"
            />
          </div>
          <button className="btn btn-primary" onClick={handleAdd} disabled={saving || !newNumber}>
            Add
          </button>
        </div>

        {error && <div className="inline-msg error">{error}</div>}

        <button className="btn btn-secondary btn-full" onClick={onClose} style={{marginTop: 12}}>
          Done
        </button>
      </div>
    </div>
  );
}

function ContactModal({ userPhone, onClose }) {
  const [category, setCategory] = useState('App Issue');
  const [message, setMessage] = useState('');
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState('');

  const handleSend = async () => {
    if (!message.trim()) return setError('Please enter a message.');
    setError('');
    setSending(true);
    try {
      const text = `📬 Ferry Tracker Feedback\nFrom: ${userPhone}\nType: ${category}\n\n${message}`;
      await apiFetch('/api/contact', {
        method: 'POST',
        body: JSON.stringify({ message: text }),
      });
      setSent(true);
    } catch (e) {
      setError(e.message);
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" onClick={e => e.stopPropagation()}>
        <h3>Contact Us</h3>
        {sent ? (
          <>
            <p className="otp-sent-msg">Thanks for your feedback! We'll get back to you on WhatsApp.</p>
            <button className="btn btn-primary btn-full" onClick={onClose}>Close</button>
          </>
        ) : (
          <>
            <div className="field">
              <label>Category</label>
              <select value={category} onChange={e => setCategory(e.target.value)}>
                <option>App Issue</option>
                <option>Feature Request</option>
                <option>Route Suggestion</option>
                <option>Notification Problem</option>
                <option>Other</option>
              </select>
            </div>
            <div className="field">
              <label>Message</label>
              <textarea
                rows={4}
                placeholder="Describe your issue or idea…"
                value={message}
                onChange={e => setMessage(e.target.value)}
                className="contact-textarea"
              />
            </div>
            {error && <div className="inline-msg error">{error}</div>}
            <button className="btn btn-primary btn-full" onClick={handleSend} disabled={sending}>
              {sending ? 'Sending…' : 'Send'}
            </button>
            <button className="btn btn-secondary btn-full" onClick={onClose} style={{marginTop: 8}}>Cancel</button>
          </>
        )}
      </div>
    </div>
  );
}

// ── Root Component ────────────────────────────────────────────────────────

export default function App() {
  const [userPhone, setUserPhone] = useState(null);

  useEffect(() => {
    const saved = getCookie('ferry_auth_phone');
    if (saved) setUserPhone(saved);
  }, []);

  const handleLogin = (phone) => {
    setUserPhone(phone);
  };

  const handleLogout = () => {
    deleteCookie('ferry_auth_phone');
    setUserPhone(null);
  };

  if (!userPhone) {
    return <LoginPage onLogin={handleLogin} />;
  }

  return <FerryTracker userPhone={userPhone} onLogout={handleLogout} />;
}
